"use client";

import CheckCompatibilityModal from "@/components/my-ui/modals/CheckCompatibilityModal";
import CompatibilitySuccessModal from "@/components/my-ui/modals/CompatibilitySuccessModal";
import { Button } from "@/components/ui/button";
import { generatePlanSlug } from "@/helpers/generatePlanSlug";
import { getGtmUserData } from "@/helpers/getGtmUserData";
import { getUniqueDataOptions } from "@/helpers/getUniqueDataOptions";
import { getUniqueValidityOptions } from "@/helpers/getUniqueValidityOptions";
import { sendGTMEvent } from "@/helpers/sendGTMEvent";
import { useAvailableEsimsForTopupMutation } from "@/hooks/useAvailableEsimsForTopup";
import { useBottomNav } from "@/hooks/useBottomNav";
import { useCartActions } from "@/hooks/useCartActions";
import { usePackageType } from "@/hooks/usePackageType";
import { usePackagesFilter } from "@/hooks/usePackagesFilter";
import { useRenewTopupPackage } from "@/hooks/useRenewTopupPackage";
import { useRouter } from "@/i18n/navigation";
import { useAuth } from "@/providers/AuthProvider";
import { dataOnlySimPath } from "@/types/constants/constants";
import { Package } from "@/types/packages/data-only/DataOnlyCountryPackages";
import { AvailableEsim } from "@/types/purchase/AvailableEsimsForTopup";
import { useCodiea } from "@codiea/tracking/react";
import { Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/** Sync a query param without useSearchParams/nuqs so SSR HTML is not replaced by a Suspense fallback. */
function replaceQueryParam(key: string, value: string | null) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (value) url.searchParams.set(key, value);
  else url.searchParams.delete(key);
  window.history.replaceState(
    null,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}
import CartSheet from "../cart/CartSheet";
import { ProductCart } from "../cart/ProductCart";
import CountryImageAndHeadings from "./CountryImageAndHeadings";
import InfoMessage from "./InfoMessage";
import PackageTypeToggle from "./PackageTypeToggle";
import PackagesFilters from "./PackagesFilters";
import PackagesGrid from "./PackagesGrid";
import PlanTypeToggle, { PlanType } from "./PlanTypeToggle";
import SelectEsimModal from "./SelectEsimModal";

function CompatibilityButton({
  label,
  shortLabel,
  onClick,
}: {
  label: string;
  shortLabel: string;
  onClick: () => void;
}) {
  return (
    <Button
      className="flex h-10 w-10 shrink-0 items-center justify-center px-0 sm:w-auto sm:gap-1.5 sm:px-2.5 sm:text-sm xl:h-11 xl:gap-2 xl:px-3"
      variant="outline"
      aria-label={label}
      onClick={onClick}
    >
      <Smartphone className="shrink-0 text-primary" size={18} />
      <span className="hidden sm:inline">{shortLabel}</span>
    </Button>
  );
}

type CountryPackagesListProps = {
  dataOnlyPackagesStandard?: Package[] | undefined;
  dataOnlyPackagesUniversal?: Package[] | undefined;
  dataVoicePackages?: Package[] | undefined;
  countryImage: string | null;
  countryFlag: string;
  countryName: string;
};

function CountryPackagesListContent({
  dataOnlyPackagesStandard,
  dataOnlyPackagesUniversal,
  dataVoicePackages,
  countryImage,
  countryFlag,
  countryName,
}: CountryPackagesListProps) {
  const t = useTranslations(`Shared`);
  const router = useRouter();
  const { isAuthenticated, user, isAuthLoading } = useAuth();
  const { track } = useCodiea();

  // Local URL state (not nuqs/useSearchParams) so package copy SSR into page source for SEO
  const [planSlug, setPlanSlugState] = useState("");
  const [simType, setSimTypeState] = useState("");
  const [urlHydrated, setUrlHydrated] = useState(false);

  const setPlanSlug = useCallback((value: string | null) => {
    const next = value ?? "";
    setPlanSlugState(next);
    replaceQueryParam("plan", next || null);
  }, []);

  const setSimType = useCallback((value: string | null) => {
    const next = value ?? "";
    setSimTypeState(next);
    replaceQueryParam("sim_type", next || null);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPlanSlugState(params.get("plan") ?? "");
    setSimTypeState(params.get("sim_type") ?? "");
    setUrlHydrated(true);
  }, []);

  const [selectedPackageId, setSelectedPackageId] = useState("");
  const planType: PlanType = simType === "universal" ? "universal" : "standard";
  const [showSelectEsimModal, setShowSelectEsimModal] = useState(false);
  const {
    mutate: fetchAvailableEsims,
    data: availableEsims,
    reset: resetAvailableEsims,
    isPending: isCheckingAvailableEsims,
  } = useAvailableEsimsForTopupMutation();

  const dataOnlyPackages =
    planType === "universal"
      ? dataOnlyPackagesUniversal
      : dataOnlyPackagesStandard;

  function handlePlanTypeChange(type: PlanType) {
    setSimType(type === "universal" ? "universal" : null);
  }

  function findPackageByPlanSlug(
    packages: Package[] | undefined,
    slug: string,
  ) {
    return packages?.find((pkg) => generatePlanSlug(pkg.name) === slug);
  }

  const [openCompatibilityModal, setOpenCompatibilityModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const { toggleBottomNave } = useBottomNav();
  const hasResolvedUrlPlan = useRef(false);
  const [
    isSelectedPackageFromVodafoneOrO2,
    setIsSelectedPackageFromVodafoneOrO2,
  ] = useState(false);

  const {
    cartQuantity,
    setCartQuantity,
    showCart,
    setShowCart,
    isCheckoutPageLoading,
    handleAddToCart,
  } = useCartActions();

  const {
    packageType,
    setPackageType,
    showPackageTypeToggle,
    isDataOnlyPackagesSelected,
    isDataVoicePackagesSelected,
    activePackages,
  } = usePackageType({
    dataOnlyPackages,
    dataVoicePackages,
  });

  const {
    dataFilter,
    validityFilter,
    filteredPackages,
    haveFilteredPackages,
    handleClearFilter,
    handleFilterByData,
    handleFilterByValidity,
    isAnyFilterApplied,
    openFilters,
    setOpenFilters,
  } = usePackagesFilter(activePackages);

  const haveDataVoicePackages = !!dataVoicePackages;
  const haveDataOnlyPackages = !!dataOnlyPackages;

  const uniqueValidityList = getUniqueValidityOptions(activePackages);
  const uniqueDataList = getUniqueDataOptions(activePackages);

  function handleSelectPackage(packageId: string) {
    setSelectedPackageId(packageId);
    setCartQuantity(1);

    const pkg = activePackages.find((p) => p.id === packageId);
    if (pkg) {
      setPlanSlug(generatePlanSlug(pkg.name));
    }
  }

  useEffect(() => {
    if (selectedPackageId) {
      const selectedPackage = activePackages.find(
        (pkg) => pkg.id === selectedPackageId,
      );
      setSelectedPackage(selectedPackage ?? null);
      if (selectedPackage) {
        setIsSelectedPackageFromVodafoneOrO2(
          ["Esim.net"].includes(selectedPackage.provider),
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackageId]);

  useEffect(() => {
    if (isAuthLoading || activePackages.length === 0) return;

    const item = activePackages[0];
    const { name, price, package_type, id } = item;

    const viewedItems = [
      {
        item_id: id,
        item_name: name,
        price: price,
        quantity: 1,
        affiliation: "eSIMCard Web",
        item_brand: "eSIMCard",
        item_category: package_type,
      },
    ];

    sendGTMEvent({
      event: "view_item",
      user_data: getGtmUserData(user?.name),
      ecommerce: {
        currency: "USD",
        value: price,
        items: viewedItems,
      },
    });

    void track({
      event: "view_item",
      userId: user?.id != null ? String(user.id) : undefined,
      properties: {
        plan_id: String(id),
        value: price,
        currency: "USD",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      toggleBottomNave(false);
    }

    return () => {
      toggleBottomNave(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!urlHydrated) return;

    function trySwitchForDeepLink(slug: string): boolean {
      if (
        packageType === "data-only" &&
        findPackageByPlanSlug(dataVoicePackages, slug)
      ) {
        setPackageType("data-voice");
        setSimType(null);
        return true;
      }

      if (packageType === "data-voice") return false;

      if (
        planType === "standard" &&
        findPackageByPlanSlug(dataOnlyPackagesUniversal, slug)
      ) {
        setSimType("universal");
        return true;
      }
      if (
        planType === "universal" &&
        findPackageByPlanSlug(dataOnlyPackagesStandard, slug)
      ) {
        setSimType(null);
        return true;
      }
      return false;
    }

    const urlPackage = planSlug
      ? findPackageByPlanSlug(filteredPackages, planSlug)
      : undefined;

    if (
      !urlPackage &&
      !hasResolvedUrlPlan.current &&
      planSlug &&
      trySwitchForDeepLink(planSlug)
    ) {
      return;
    }

    hasResolvedUrlPlan.current = true;

    if (!haveFilteredPackages) {
      setSelectedPackageId("");
      setSelectedPackage(null);
      return;
    }

    if (urlPackage) {
      setSelectedPackageId(urlPackage.id);
      setCartQuantity(1);
      return;
    }

    const firstPackage = filteredPackages[0];
    setSelectedPackageId(firstPackage.id);
    setCartQuantity(1);
    setPlanSlug(generatePlanSlug(firstPackage.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlHydrated,
    planType,
    packageType,
    dataFilter,
    validityFilter,
    planSlug,
  ]);

  useEffect(() => {
    resetAvailableEsims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType]);

  function openEsimSelection(esims: AvailableEsim[] | undefined) {
    if (esims && esims.length > 0) {
      setShowSelectEsimModal(true);
    } else {
      handleBuyNewEsim();
    }
  }

  function handleBuyNowClick() {
    if (!selectedPackageId) {
      toast.error("Please select a package first.");
      return;
    }

    if (!isAuthenticated || planType !== "universal") {
      handleBuyNewEsim();
      return;
    }

    if (availableEsims) {
      openEsimSelection(availableEsims);
      return;
    }

    fetchAvailableEsims(undefined, {
      onSuccess: (esims) => openEsimSelection(esims),
      onError: (error: Error) => toast.error(error.message),
    });
  }

  function handleBuyNewEsim() {
    handleAddToCart({
      activePackages,
      countryFlag,
      selectedPackageId,
      goToCheckout: true,
    });
  }

  const { renewTopup, isRenewingTopup } = useRenewTopupPackage({
    onRenewed: (data) => {
      setShowSelectEsimModal(false);
      if (data.status === "Completed" && data.simIccid) {
        router.push(`${dataOnlySimPath}${data.simIccid}/`);
      }
    },
    backHref: typeof window !== "undefined" ? window.location.href : undefined,
  });

  function handleContinueWithExistingEsim(iccid: string) {
    if (!selectedPackageId) return;
    renewTopup({ iccid, packageId: selectedPackageId });
  }

  const packagesFilters = (
    <PackagesFilters
      dataFilter={dataFilter}
      dataFilterOptions={uniqueDataList}
      handleClearFilter={handleClearFilter}
      handleFilterByData={handleFilterByData}
      handleFilterByValidity={handleFilterByValidity}
      isAnyFilterApplied={isAnyFilterApplied}
      openFilters={openFilters}
      setOpenFilters={setOpenFilters}
      validityFilter={validityFilter}
      validityFilterOptions={uniqueValidityList}
      packages={activePackages}
      iconOnlyBelowXl={!showPackageTypeToggle}
    />
  );

  return (
    <>
      <div className="grid w-full gap-x-10 gap-y-0 xl:grid-cols-[minmax(0,500px)_1fr] xl:gap-y-10">
        {/* country image — sticky while packages column scrolls */}
        <div className="xl:sticky xl:top-[120px] xl:z-10 xl:self-start">
          <CountryImageAndHeadings
            countryFlag={countryFlag}
            countryImage={countryImage}
            countryName={countryName}
          />
        </div>

        {/* Country packages  */}
        <div className="flex w-full flex-col gap-3 md:gap-5 xl:gap-5">
          {/* Desktop heading */}
          <h2 className="hidden text-start font-montserrat text-lg font-600 md:text-[1.25rem] xl:block">
            {t(`countryPackages.withPackages`, { countryName: countryName })}
          </h2>

          {/* Data / voice toggle + check compatibility (mobile) */}
          {showPackageTypeToggle && (
            <div className="flex w-full items-center justify-between gap-2 sm:gap-3">
              <div className="min-w-0 flex-1 xl:flex-none">
                <PackageTypeToggle
                  packageType={packageType}
                  setPackageType={setPackageType}
                  setCartQuantity={setCartQuantity}
                  handleClearFilter={handleClearFilter}
                  onSwitchToDataVoice={() => {
                    if (planType === "universal") {
                      handlePlanTypeChange("standard");
                    }
                  }}
                />
              </div>

              <div className="shrink-0 xl:hidden">
                <CompatibilityButton
                  label={t("compatibleModal.btnTitle")}
                  shortLabel={t("compatibleModal.btnTitleShort")}
                  onClick={() => setOpenCompatibilityModal(true)}
                />
              </div>
            </div>
          )}

          {/* standard / universal + filter (+ check compatibility) */}
          <div className="flex w-full items-center justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              <PlanTypeToggle
                planType={planType}
                setPlanType={handlePlanTypeChange}
                disableUniversal={isDataVoicePackagesSelected}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <div className="hidden xl:block">
                <CompatibilityButton
                  label={t("compatibleModal.btnTitle")}
                  shortLabel={t("compatibleModal.btnTitleShort")}
                  onClick={() => setOpenCompatibilityModal(true)}
                />
              </div>
              {packagesFilters}
              {!showPackageTypeToggle && (
                <div className="shrink-0 xl:hidden">
                  <CompatibilityButton
                    label={t("compatibleModal.btnTitle")}
                    shortLabel={t("compatibleModal.btnTitleShort")}
                    onClick={() => setOpenCompatibilityModal(true)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Country Packages — full height, page scrolls */}
          <div>
            {haveFilteredPackages && (
              <>
                <PackagesGrid
                  countryFlag={countryFlag}
                  countryName={countryName}
                  filteredPackages={filteredPackages}
                  handleValueChange={handleSelectPackage}
                  isDataOnlyPackagesSelected={isDataOnlyPackagesSelected}
                  isDataVoicePackagesSelected={isDataVoicePackagesSelected}
                  selectedPackageId={selectedPackageId}
                  setSelectedPackageId={handleSelectPackage}
                  haveFilteredPackages={haveFilteredPackages}
                />
              </>
            )}

            {!haveDataOnlyPackages && !haveDataVoicePackages && (
              <InfoMessage message={t(`countryPackages.outOfStock`)} />
            )}
          </div>

          {/* Increament decreament and add to cart buttons  */}
          {/* {(dataOnlyPackages || dataVoicePackages) && (
            <div
              className="grid grid-cols-2 gap-4 px-4 md:grid-cols-[minmax(120px,200px)_1fr_1fr] md:px-0 xl:gap-6"
              id="addToCart"
            >
              <IncDecButtons
                cartQuantity={cartQuantity}
                setCartQuantity={setCartQuantity}
              />

              <Button
                aria-label={"Add to cart"}
                className={`group order-1 flex h-full w-full items-center gap-3 text-sm`}
                onClick={() =>
                  handleAddToCart({
                    activePackages,
                    countryFlag,
                    selectedPackageId,
                  })
                }
                variant={"outline"}
              >
                <Plus
                  className="shrink-0 transition group-hover:rotate-90 group-hover:text-foreground"
                  size={20}
                />
                {t("countryPackages.addToCart")}
              </Button>

              <Button
                aria-label={"Buy now"}
                className={`group order-2 col-span-2 flex h-full w-full items-center gap-3 text-sm md:order-1 md:col-span-1`}
                onClick={handleBuyNowClick}
                disabled={isCheckingAvailableEsims}
              >
                {isCheckingAvailableEsims ? (
                  <SpinnerMini />
                ) : (
                  <>
                    {t("countryPackages.buyNow")}
                    <ArrowUpRight
                      className="transition group-hover:rotate-45 group-hover:text-foreground"
                      size={20}
                    />
                  </>
                )}
              </Button>
            </div>
          )} */}

          {isSelectedPackageFromVodafoneOrO2 && (
            <InfoMessage
              message={t("countryPackages.vodafoneInfoText")}
              className="min-h-max text-sm"
            />
          )}
        </div>
      </div>

      {/* Add to cart side panel  or sheet */}
      <CartSheet setShowCart={setShowCart} showCart={showCart} />

      {/* Check compatibility modal */}
      <CheckCompatibilityModal
        open={openCompatibilityModal}
        setOpen={setOpenCompatibilityModal}
        setShowSuccessModal={setShowSuccessModal}
        setSelectedDevice={setSelectedDevice}
      />

      <CompatibilitySuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        deviceName={selectedDevice}
      />

      {selectedPackage && (
        <ProductCart
          selectedPackage={selectedPackage}
          countryFlag={countryFlag}
          isCheckoutPageLoading={
            isCheckoutPageLoading || isCheckingAvailableEsims
          }
          cartQuantity={cartQuantity}
          setCartQuantity={setCartQuantity}
          onAddToCart={() =>
            handleAddToCart({
              activePackages,
              countryFlag,
              selectedPackageId,
            })
          }
          onBuyNow={handleBuyNowClick}
        />
      )}

      <SelectEsimModal
        open={showSelectEsimModal}
        setOpen={setShowSelectEsimModal}
        onBuyNewEsim={handleBuyNewEsim}
        onContinueWithExistingEsim={handleContinueWithExistingEsim}
        isContinuing={isRenewingTopup}
        availableEsims={availableEsims ?? null}
      />
    </>
  );
}

export default function CountryPackagesList(props: CountryPackagesListProps) {
  return <CountryPackagesListContent {...props} />;
}
